import {
  Alert,
  AlertTitle,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";

/**
 * Card #2434 AC4: surface a dropped row to the user instead of failing open.
 *
 * uploadResults returns `droppedRows` (plus `summary.droppedRowCount`) for
 * every row it could not place in a class, and for every class that is in the
 * uploaded file but had no result group built for it. Before this, nothing
 * rendered either field: the show office saw a clean "Publish Preview" while
 * a class had quietly vanished, which is how comp 346492's class 316 reached
 * USEF missing its only ride and nobody noticed for days.
 *
 * Renders nothing when there is nothing dropped, so it can sit
 * unconditionally in both the preview view and the upload form view.
 *
 * @param {{ rows?: Array<{
 *   rowIndex?: number,
 *   reason?: string,
 *   USEFCompID?: string|null,
 *   ClassNumber?: string|null,
 *   ClassTitle?: string|null,
 *   ExhibitorNumber?: string|null,
 *   RiderName?: string|null,
 * }> }} props
 */
export default function DroppedRowsAlert({ rows }) {
  const { t } = useTranslation("common");

  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning" data-testid="dropped-rows-alert">
      <AlertTitle>{t("results.upload.droppedRows.title")}</AlertTitle>
      <Typography variant="body2" data-testid="dropped-rows-count">
        {rows.length === 1
          ? t("results.upload.droppedRows.single", { count: rows.length })
          : t("results.upload.droppedRows.plural", { count: rows.length })}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {t("results.upload.droppedRows.message")}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small" data-testid="dropped-rows-table">
          <TableHead>
            <TableRow>
              <TableCell align="right">
                {t("results.upload.droppedRows.columns.rowIndex")}
              </TableCell>
              <TableCell>
                {t("results.upload.droppedRows.columns.classId")}
              </TableCell>
              <TableCell>
                {t("results.upload.droppedRows.columns.className")}
              </TableCell>
              <TableCell>
                {t("results.upload.droppedRows.columns.exhibitorNumber")}
              </TableCell>
              <TableCell>
                {t("results.upload.droppedRows.columns.riderName")}
              </TableCell>
              <TableCell>
                {t("results.upload.droppedRows.columns.reason")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={`${row.rowIndex ?? "na"}-${row.ClassNumber ?? "na"}-${index}`}
              >
                {/* rowIndex is the API's own position within the parsed file.
                    Rendered exactly as sent, never incremented: inventing a
                    +1 here would report a row number the API never said. */}
                <TableCell align="right">
                  {row.rowIndex ?? "-"}
                </TableCell>
                <TableCell>{row.ClassNumber || "-"}</TableCell>
                <TableCell>{row.ClassTitle || "-"}</TableCell>
                <TableCell>{row.ExhibitorNumber || "-"}</TableCell>
                <TableCell>{row.RiderName || "-"}</TableCell>
                <TableCell>
                  <Box component="span">{row.reason || "-"}</Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Alert>
  );
}
